import md5 from 'js-md5';
import gql from 'graphql-tag';
import i18n from '@assets/i18n';
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery, useMutation } from '@vue/apollo-composable';
import baseConfig from '@config/base.config';
import urlUtils from '@utils/url.utils';
import cookieUtils from '@utils/cookie.utils';
import { updateLocalUserInfo } from '@master/utils/account.util';
import { changeLocale } from '@assets/i18n/index';

import en from '@ui/locale/lang/en';
import cn from '@ui/locale/lang/zh-cn';
import ja from '@ui/locale/lang/ja';
import uiLocal from '@ui/locale/index';
import useEventBus from '@assets/plugins/eventBus';

export type Language = 'EN' | 'JP' | 'ZH';

export enum PermissionsStatus {
    OFF = -1, //功能下线，所有人都不会看到
    TEST = 0, //灰度上线，有对应权限才能看到
    ON = 1 //功能上线，所有人都可以看到
}

export type PermissionsType =
    | 'campaignReportV2'
    | 'engage2.0'
    | 'engageInline'
    | 'engageLayout'
    | 'engagePreview'
    | 'engageVariant'
    | 'popupForm'
    | 'popupTemplate'
    | 'userGroupTimes'
    | 'goalRex';

export interface PermissionsDict {
    id: string | PermissionsType;
    updateTime: number;
    description: string;
    name: string;
    status: PermissionsStatus;
}

export interface LoginInput {
    email: string;
    password: string;
    beFromGoogle?: boolean;
    googleUserToken?: string;
    rememberMe?: boolean;
}

enum AccountStatus {
    INVALID = 0,
    EFFECTIVE = 1,
    DELETED = -1
}

type Account = {
    id: string;
    ptiUid: string;
    firstName: string;
    name: string;
    email: string;
    password: string;
    phone: string;
    industry: string;
    company: string;
    position: string;
    websiteType: string;
    token: string;
    hasActivated: boolean;
    hasAllow: boolean;
    createTime: number;
    activatedTime: number;
    updateTime: number;
    signupSource: string;
    permissions: Array<PermissionsType>;
    permissionsDict: Array<PermissionsDict>;
    i18n: string;
    status: AccountStatus;
    freeTrialLong: number;
    accountType: string;
    department: string;
    companySize: string;
    address: string;
    onlyPti: boolean;
    jobTitle: string;
    loginUtm: string;
    needSecondVerification: boolean;
    beFirstLogin: boolean;
    needGuide: boolean;
    ptiToken: string;
    ptiPwd: string;
    role: string;
    area: string;
    useType: number;
    beFromGoogle: number;
    isNpPackage: boolean;
    isProductIntroPopup: boolean;
    registerUtm: any;
};

type NewPtengineAccount = {
    id: string;
    ptiUid: string;
    email: string;
    name: string;
    firstName: string;
    language: Language;
    industry: string;
    companyName: string;
    companySize: string;
    department: string;
    jobTitle: string;
    role: string;
    telphone: string;
    address: string;
    totalProfile: number;
    consumedProfile: number;
    industryList: string;
    permissions: Array<PermissionsType>;
    permissionsDict: Array<PermissionsDict>;
    area: string;
    beFromGoogle: boolean;
    useType: number;
    signupSource: string;
    isNpPackage: boolean;
    ptiPwd: string;
};

type NewPtengineAccountUpdateInput = {
    id?: string;
    ptiUid?: string;
    email?: string;
    firstName?: string;
    name?: string;
    language?: string;
    industry?: string;
    companyName?: string;
    companySize?: string;
    department?: string;
    jobTitle?: string;
    telphone?: string;
    address?: string;
    role?: string;
    useType?: number;
};

const ACCOUNT_QUERY = gql`
    query getNewPtengineAccount {
        getNewPtengineAccount {
            id
            ptiUid
            ptiPwd
            email
            name
            firstName
            language
            industry
            companyName
            companySize
            department
            jobTitle
            role
            telphone
            address
            totalProfile
            consumedProfile
            industryList
            permissionsDict
            permissions
            area
            beFromGoogle
            useType
            signupSource
            isNpPackage
        }
    }
`;

const LOGIN_QUERY = gql`
    query newPtengineLogin($data: AccountLoginInput!, $where: AccountUnionWhereUniqueInput!) {
        newPtengineLogin(data: $data, where: $where) {
            id
            ptiUid
            firstName
            name
            email
            password
            phone
            industry
            company
            position
            websiteType
            token
            hasActivated
            hasAllow
            createTime
            activatedTime
            updateTime
            signupSource
            permissionsDict
            permissions
            i18n
            status
            freeTrialLong
            accountType
            department
            companySize
            address
            onlyPti
            jobTitle
            loginUtm
            needSecondVerification
            beFirstLogin
            isProductIntroPopup
            needGuide
            ptiToken
            ptiPwd
            role
            area
            useType
            beFromGoogle
        }
    }
`;

const UPDATE_ACCOUNT_MUTATION = gql`
    mutation updateAccountFromNewPtengine($data: NewPtengineAccountUpdateInput!) {
        updateAccountFromNewPtengine(data: $data) {
            id
            ptiUid
            firstName
            name
            email
            password
            phone
            industry
            company
            position
            websiteType
            token
            hasActivated
            hasAllow
            createTime
            activatedTime
            updateTime
            signupSource
            permissions
            permissionsDict
            i18n
            status
            freeTrialLong
            accountType
            department
            companySize
            address
            onlyPti
            jobTitle
            loginUtm
            needSecondVerification
            beFirstLogin
            needGuide
            ptiToken
            ptiPwd
            role
            area
            useType
            beFromGoogle
            isNpPackage
            isProductIntroPopup
            registerUtm
        }
    }
`;

const { eventBus } = useEventBus();
const domain = urlUtils.getDomain(location.host) || import.meta.env.VITE_COOKIE_DOMAIN;

const updateAccountInfoLoading = ref(false);
export const accountInfo = ref<NewPtengineAccount | undefined>();

export function useAccountInfo() {
    // 登录
    const login = (
        params: LoginInput,
        resultCallback: ({}: Account) => void,
        rejectCallback: (err: any) => void
    ) => {
        const route = useRoute();
        const { onResult, onError } = useQuery<{ newPtengineLogin: Account }>(LOGIN_QUERY, {
            where: {
                email: params.email,
                password: md5(params.password),
                beFromGoogle: params.beFromGoogle,
                token: params?.googleUserToken
            },
            data: { remeberMe: params?.rememberMe, routeQuery: route.query }
        });

        onResult(data => {
            if (data?.data?.newPtengineLogin) {
                resultCallback(data.data.newPtengineLogin);
            } else rejectCallback(data?.data);
        });

        onError(err => {
            rejectCallback(err);
        });
    };

    // 获取账号信息
    const getAccountInfo = (
        resultCallback?: (T?: NewPtengineAccount) => void,
        rejectCallback?: (err?: any) => void
    ) => {
        const { onResult, onError } =
            useQuery<{ getNewPtengineAccount: NewPtengineAccount }>(ACCOUNT_QUERY);

        onResult(data => {
            if (data?.data?.getNewPtengineAccount) {
                _updateAccount(data.data.getNewPtengineAccount);
                resultCallback &&
                    typeof resultCallback === 'function' &&
                    resultCallback(accountInfo.value);
            }
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };
    // 更新账号信息

    const updateAccountInfo = async (data: NewPtengineAccountUpdateInput) => {
        const { mutate, loading } = getMutate();
        updateAccountInfoLoading.value = loading.value;

        const result = (await mutate({ data }))?.data?.updateAccountFromNewPtengine;
        if (result?.id) {
            _updateAccount(Object.assign({}, accountInfo.value, data));
            return true;
        }
        return result;
    };

    // 动态计算，匹配当前aid
    const account = computed(() => {
        const aid = getLocalAccountId();
        return accountInfo.value?.id === aid ? accountInfo.value : null;
    });

    return {
        account,
        updateAccountInfoLoading,

        login,
        getAccountInfo,
        updateAccountInfo
    };
}

// 修改语言设置
watch(
    () => accountInfo.value,
    (newData, oldData) => {
        if (!newData) return;

        if (newData.language !== oldData?.language) {
            changeLocale(newData.language);
            uiLocal.use(newData.language === 'JP' ? ja : newData.language === 'ZH' ? cn : en);
        }

        const { ACCOUNT_I18N } = baseConfig;
        localStorage.setItem(ACCOUNT_I18N, newData.language);
        cookieUtils.setItem({
            sKey: ACCOUNT_I18N,
            sValue: newData.language,
            sDomain: String(domain)
        });

        // 更新pti语言
        eventBus.$emit('insightChangeLanguage', newData.language?.toLocaleLowerCase());
    },
    {
        immediate: true
    }
);

function getMutate() {
    const { mutate, loading } =
        useMutation<{ updateAccountFromNewPtengine: Account }>(UPDATE_ACCOUNT_MUTATION);

    return {
        mutate,
        loading
    };
}

export function updateLocalAccountId(aid: string) {
    localStorage.setItem(baseConfig.ACCOUNT_ID, aid);
}

export function getLocalAccountId() {
    return localStorage.getItem(baseConfig.ACCOUNT_ID);
}

function _updateAccount(data: NewPtengineAccount) {
    accountInfo.value = _updateUserInfo(data);
    updateLocalUserInfo(data as any);
    updateLocalAccountId(accountInfo.value.id);
}

function _updateUserInfo(accountInfo: NewPtengineAccount) {
    return Object.assign({}, accountInfo, {
        industryList: accountInfo?.industryList
            ? _getIndustryList(accountInfo?.industryList, accountInfo.language)
            : []
    });
}

function _getIndustryList(list: string | [], language: string) {
    let result: any = [];
    (Array.isArray(list) ? list : JSON.parse(list)).map((item: any) => {
        item.id >= 100 &&
            result.push({
                id: item.id,
                name: language?.toLocaleLowerCase() === 'jp' ? item.nameJp : item.nameEu
            });
    });
    return result;
}
